<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import Image from '~/components/atoms/Image.vue'
import Text from '~/components/atoms/Text.vue'
import { PRODUCT_VIEW_BLOCKS, saveProductViewContext } from '~/utils/productViewContext.js'

const props = defineProps({
  sku: String,
  alternative_products: {
    type: Object,
    required: true
  }
})

const emits = defineEmits(['hover', 'unhover'])

const color = ref(props.alternative_products?.products?.[0]?.color ?? null)
const hoveredColor = ref(null)
const showMore = ref(false)

let timer = null
let mediaQuery = null

const canHover = ref(false)

const updateCanHover = () => {
  if (typeof window === 'undefined') {
    canHover.value = false
    return
  }

  canHover.value = window.matchMedia('(hover: hover) and (pointer: fine)').matches
}

const hover = (product) => {
  if (!canHover.value || product.sku === props.sku) {
    return
  }

  clearTimeout(timer)
  hoveredColor.value = product.color
  emits('hover', product.full_image)
}

const unHover = () => {
  if (!canHover.value) {
    return
  }

  clearTimeout(timer)

  timer = setTimeout(() => {
    hoveredColor.value = null
    emits('unhover')
  }, 500)
}

const onAlternativeClick = (product, index) => {
  saveProductViewContext({
    productId: product.id,
    position: index + 1,
    block: PRODUCT_VIEW_BLOCKS.productPageColors,
  })
}

const visibleProducts = computed(() => {
  const products = props.alternative_products?.products ?? []

  if (showMore.value) {
    return products
  }

  return products.slice(0, 6)
})

const hiddenCount = computed(() => {
  const total = props.alternative_products?.products?.length ?? 0
  return total > 6 ? total - 5 : 0
})

onMounted(() => {
  updateCanHover()

  if (typeof window !== 'undefined') {
    mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)')

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateCanHover)
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(updateCanHover)
    }
  }
})

onBeforeUnmount(() => {
  clearTimeout(timer)

  if (mediaQuery) {
    if (typeof mediaQuery.removeEventListener === 'function') {
      mediaQuery.removeEventListener('change', updateCanHover)
    } else if (typeof mediaQuery.removeListener === 'function') {
      mediaQuery.removeListener(updateCanHover)
    }
  }
})
</script>

<template>
  <div v-if="alternative_products?.products?.length" class="g-8">
    <Text class="sign">
      {{ alternative_products.alternative_main_sign }}:
      <strong>{{ hoveredColor ?? color }}</strong>
    </Text>

    <div class="row g-8 wrap the-same-gap alternatives-grid">
      <template v-for="(alternative_product, index) in visibleProducts" :key="alternative_product.sku || alternative_product.id || index">
        <nuxt-link
            :to="'/shop/' + alternative_product.url"
            class="alternative-link aspect-1 hbb"
            :class="{ active: alternative_product.sku === props.sku }"
            @click="onAlternativeClick(alternative_product, index)"
            @auxclick="onAlternativeClick(alternative_product, index)"
        >
          <div
              class="thumb"
              @mouseenter="hover(alternative_product)"
              @mouseleave="unHover"
          >
            <Image :src="alternative_product.image" />

            <div
                v-if="!showMore && index === 5 && alternative_products.products.length > 6"
                class="open-more flex-center"
                @click.stop.prevent="showMore = true"
            >
              <Text tag="strong">+ {{ hiddenCount }}</Text>
            </div>
          </div>
        </nuxt-link>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.alternatives-grid {
  padding-right: 40px;
}

.alternative-link {
  width: 55px;
  display: block;
  position: relative;
}

.thumb {
  position: relative;
  width: 100%;
  height: 100%;
}

.open-more {
  position: absolute;
  inset: 0;
  background: rgba(52, 52, 52, 0.52);
  color: white;
  padding: 4px 8px;
  font-size: 18px;
}

.sign {
  color: $light-black;
  font-size: 13px;

  strong {
    color: $dark-black;
  }

  @include smd {
    font-size: 12px;
  }
}
</style>
